// Workspace Management Types (Phase 4: wired to OpenAPI types)
import type {
  Workspace as GeneratedWorkspace,
  WorkspaceContext as GeneratedWorkspaceContext,
} from "@/lib/generated/types.gen";

// Align local Workspace with generated schema and keep optional aggregates while
// backend payloads continue to supply operational metadata.
export type Workspace = GeneratedWorkspace & {
  total_datasets?: number;
  total_signatures?: number;
  // Preserve non-null string for billing_status in legacy views
  billing_status?: string;
};

type GeneratedWorkspaceContextWithoutWorkspace = Omit<
  GeneratedWorkspaceContext,
  "workspace" | "role"
>;

// Thin alias around the generated context payload so consumers operate directly on
// the v3 schema while still benefitting from the extended Workspace shape.
export type WorkspaceContextResponse = GeneratedWorkspaceContextWithoutWorkspace & {
  workspace: Workspace;
  role: WorkspaceRole;
  permissions?: string[];
};

export type WorkspaceRole = "admin" | "member" | "readonly";

export const ROLE_HIERARCHY: Record<WorkspaceRole, number> = {
  admin: 80,
  member: 60,
  readonly: 40,
};

export const FEATURES = {
  AI_FORM_GENERATION: "ai_form_generation",
  BRANDED_WORKSPACE: "branded_workspace",
  ELECTRONIC_SIGNATURES: "electronic_signatures",
  ADVANCED_API: "advanced_api",
  WEBHOOK_INTEGRATION: "webhook_integration",
  CUSTOM_BRANDING: "custom_branding",
  ADVANCED_ANALYTICS: "advanced_analytics",
  BULK_OPERATIONS: "bulk_operations",
  PRIORITY_SUPPORT: "priority_support",
  WHITE_LABEL: "white_label",
} as const;

export interface CreateWorkspaceRequest {
  name: string;
  description?: string;
  slug?: string;
}

// Use the OpenAPI-generated error shape to avoid drift.
// Keep optional legacy `error_code` for backward compatibility if older
// services still return it.
import type { ErrorResponse as GeneratedErrorResponse } from "@/lib/generated/types.gen";
export type ErrorResponse = GeneratedErrorResponse & { error_code?: string };

// Re-export the ApiError type for historical consumers transitioning off the old classes
export type { ApiError } from "@/lib/api-errors";

// Type Guards
export function isWorkspaceRole(role: string): role is WorkspaceRole {
  return ["admin", "member", "readonly"].includes(role);
}

export function isWorkspace(obj: unknown): obj is Workspace {
  if (typeof obj !== "object" || obj === null) return false;
  const o = obj as Record<string, unknown>;
  return (
    typeof o["id"] === "number" &&
    typeof o["company_id"] === "number" &&
    typeof o["name"] === "string" &&
    typeof o["slug"] === "string" &&
    typeof o["plan"] === "string" &&
    typeof o["is_active"] === "boolean" &&
    typeof o["created_at"] === "string" &&
    // Optional/legacy fields tolerated
    (o["billing_status"] === undefined || typeof o["billing_status"] === "string") &&
    (o["total_datasets"] === undefined || typeof o["total_datasets"] === "number") &&
    (o["total_signatures"] === undefined || typeof o["total_signatures"] === "number")
  );
}

export function isWorkspaceContextResponse(obj: unknown): obj is WorkspaceContextResponse {
  if (typeof obj !== "object" || obj === null) return false;
  const o = obj as Record<string, unknown>;
  const joinedAt = o["joined_at"];
  return (
    isWorkspace(o["workspace"]) &&
    typeof o["user_id"] === "string" &&
    typeof o["role"] === "string" &&
    isWorkspaceRole(o["role"] as string) &&
    (o["permissions"] === undefined || Array.isArray(o["permissions"])) &&
    Array.isArray(o["enabled_features"]) &&
    typeof o["is_member"] === "boolean" &&
    (joinedAt === undefined || joinedAt === null || typeof joinedAt === "string")
  );
}

export function assertWorkspaceContextResponse(input: unknown): WorkspaceContextResponse {
  if (!isWorkspaceContextResponse(input)) {
    throw new Error("Unexpected workspace context payload from backend");
  }
  return input;
}

// Utility functions
export function hasFeature(enabledFeatures: string[], required: string): boolean {
  return enabledFeatures.includes(required);
}

export function isRoleAtLeast(userRole: WorkspaceRole, requiredRole: WorkspaceRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}
